import { css } from "@emotion/core";
import styled from "@emotion/styled";
import { motion, useAnimation } from "framer-motion";
import { bool, func, number, shape, string } from "prop-types";
import React, { useEffect, useRef, useState } from "react";

import theme from "../../../theme";
import Heading from "../../Heading";
import { ImageType } from "../../ImageComponent";
import { bodySmall } from "../../Typography";

const Container = styled(motion.div)(
  ({ layer, active }) => css`
    position: relative;
    z-index: ${active ? 90 : layer};
    cursor: pointer;
    transition: ${theme.transition()};
  `
);

const Image = styled.img`
  position: relative;
  width: 100%;
  object-fit: cover;
  object-position: 50% 50%;
  vertical-align: middle;
  z-index: 6;
`;

const Overlay = styled.div(
  ({ show }) => css`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    opacity: ${show ? 0.75 : 0};
    background-color: ${theme.color("primary")};
    transition: ${theme.transition()};
    z-index: 7;
  `
);

const Popup = styled.div(
  ({ show, changeDirection }) => css`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    padding: ${theme.spacing("s")};
    opacity: ${show ? 1 : 0};
    background-color: ${theme.color("background.dark")};
    transition: ${theme.transition()};
    transform: translateX(${show ? (changeDirection ? "-100%" : "100%") : "0"});
    z-index: 5;
  `
);

const Name = styled(Heading)`
  color: #fff;
`;

const JobTitle = styled.div`
  ${bodySmall};
  margin: 0.5rem 0 0;
  color: ${theme.color("secondary")};
  overflow-wrap: break-word;
  word-wrap: break-word;
  hyphens: auto;
`;

const Person = ({
  data,
  index,
  onTapped,
  flipSlide,
  activeGrid,
  activeSibling = null,
}) => {
  const [active, setActive] = useState(false);
  const containerRef = useRef();
  const controls = useAnimation();

  const hoverEnd = () => {
    controls.start("reset");
    setActive(false);
  };

  const hoverStart = () => {
    controls.start("hover");
    setActive(true);
  };

  useEffect(() => {
    index === activeSibling ? hoverStart() : hoverEnd();
    // eslint-disable-next-line
  }, [activeSibling]);

  const containerMotion = {
    rest: {
      transition: theme.get("animation.staggeredReveal.transition"),
    },
    hover: {
      transition: theme.get("animation.staggeredReveal.transition"),
    },
    reset: {
      transition: theme.get("animation.staggeredHide.transition"),
    },
  };

  return (
    <Container
      initial="rest"
      animate={controls}
      onHoverEnd={hoverEnd}
      onHoverStart={hoverStart}
      onTap={onTapped}
      variants={containerMotion}
      ref={containerRef}
      active={active}
      layer={index}
    >
      <Overlay
        show={(activeSibling !== null && !active) || (activeGrid && !active)}
      />
      <Image
        show={(activeSibling !== null && !active) || (activeGrid && !active)}
        src={data.image.data.src}
        alt={data.firstName}
      />
      <Popup changeDirection={(index + 1) % flipSlide === 0} show={active}>
        <Name size={4}>{`${data.firstName} ${data.lastName}`}</Name>
        <JobTitle>{data.jobTitle}</JobTitle>
      </Popup>
    </Container>
  );
};

export const PersonType = {
  firstName: string,
  lastName: string,
  jobTitle: string,
  image: shape(ImageType),
};

Person.propTypes = {
  index: number,
  data: shape(PersonType),
  layer: number,
  activeSibling: number,
  onTapped: func,
  flipSlide: number,
  activeGrid: bool,
};

export default Person;
